import { ButtonCancel, SubmitButton } from "@/components/common/Button";
import { FormSingleColumn } from "@/components/specific/pipeline/FormSingleColumn";
import { useForm } from "react-hook-form";
import { UpdateWebhook, Webhook, WebhookEvent } from "@/types/domain";
import { FormHeader } from "@/components/specific/pipeline/FormHeader";
import { Label } from "@/components/common/FormLabel";
import { ErrorLabel } from "@/components/common/ErrorLabel";
import { Input } from "@/components/common/Input";
import { LoadingOverlay, LoadingPage } from "@/components/common/Loading";
import { useEffect, useState } from "react";
import { getWebhook, getWebhookEvents, updateWebhook } from "@/api/webhook";
import { showSuccessToast } from "@/hooks/use-toast";
import { Alert } from "@/components/common/Alert/Alert";
import { useNavigate, useParams } from "react-router";
import { WebhookEventSelector } from "@/components/specific/webhook/WebhookEventSelector";
import { WebhookGuidanceAccordion } from "@/components/specific/webhook/WebhookGuidanceAccordion";

export const WebhookMaintenanceEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { handleSubmit, formState, register, setValue } = useForm<UpdateWebhook>();
  const [alert, setAlert] = useState<string | null>(null);
  const [loading, setIsLoading] = useState(false);

  const [webhookEvents, setWebhookEvents] = useState<WebhookEvent[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchWebhookEvents = async () => {
      try {
        setIsLoading(true);
        setWebhookEvents(await getWebhookEvents());
      } catch (err) {
        setAlert("Failed to load webhook events, please reload the page.");
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      fetchWebhookEvents();
    }, []);

  const groupedEvents = webhookEvents.reduce<Record<string, WebhookEvent[]>>((group, event) => {
    const category = event.categoryName || "Uncategorised";
    if(!group[category])
    {
      group[category] = [];
    }
    group[category].push(event);
    return group;
  }, {}); //Always provide initial value to reduce when using TypeScript with. This resolves the type inference issue.

  const fetchWebhook = async (id: string): Promise<Webhook> => {
    const webhook = await getWebhook(id) as Webhook;
    
    // Set the basic form fields
    setValue('id', webhook.id);
    setValue('url', webhook.url);
    setValue('name', webhook.name);
    setValue('description', webhook.description);
    //setValue('secret', webhook.secret);
    setValue('isActive', webhook.isActive);
    
    webhook.eventIds?.forEach(eventId => {
      setValue(`eventIds.${eventId}`, eventId);
    });
    
    return webhook;
  };

  useEffect(() => {
    if (id) {
      fetchWebhook(id);
    }
  }, [id]);
  
  const onSubmit = async (data: UpdateWebhook) => {
    try {
      setSubmitting(true);
      setAlert(null);
      // Extract selected event IDs as we don't need a map of id -> check state for the API
      const selectedEventIds = Object.entries(data.eventIds || {})
        .filter(([_, checked]) => checked)
        .map(([id]) => Number(id));

      // given we have the webhook from react-hook-form and the selected event ids
      // we can now call the API to update the webhook
      await updateWebhook({ ...data, eventIds: selectedEventIds });
      showSuccessToast("Webhook updated successfully.");
      navigate(-1);
    } catch (error) {
      setAlert("Failed to update webhook, please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <>
    <LoadingOverlay
        isVisible={submitting}
        message={"Saving Webhook!"} />

    <FormSingleColumn onSubmit={handleSubmit(onSubmit)}>
      <FormHeader
        title="Webhook"
        formInformation="Update an existing webhook. This is where you can describe your webhook and configure its options."
      >
        <SubmitButton
          text={"Update Webhook"}
          className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold" />
        <ButtonCancel text="Cancel" className="text-kPurple-500" />
      </FormHeader>
      <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
        { alert && (
          <div className="md:col-span-5">
            <Alert variant="error" persistent={true} >{alert}</Alert>
          </div>
        )}
        <div className="md:col-span-5">
          <Label forField="name" label={"Name"} />
          <p className="font-normal text-gray-600 py-1">
            The name of the webhook</p>
          <ErrorLabel error={formState.errors.name?.message} />
          <div className="md:w-[86ch]">
            <Input
              inErrorState={!!formState.errors.name}
              placeholder="Enter a name for your webhook"
              {...register("name", {
                required: "required",
                maxLength: { value: 100, message: "max 100 characters" }
              })} />
          </div>
        </div>
        <div className="md:col-span-5">
          <Label forField="url" label={"Url"} />
          <p className="font-normal text-gray-600 py-1">
            The url where the webhook will send its payloads. (NB: Include example payload structure)</p>
          <ErrorLabel error={formState.errors.url?.message} />
          <div className="md:w-[86ch]">
            <Input
              inErrorState={!!formState.errors.url}
              placeholder="Enter a URL for your webhook"
              {...register("url", {
                required: "required",
                maxLength: { value: 500, message: "max 500 characters" },
                pattern: {
                  value: /^https:\/\/[^\s$.?#].[^\s]*$/i,
                  message: "Please enter a valid URL starting with https://",
                }
              })} />
          </div>
        </div>
        <div className="md:col-span-5">
          <Label forField="description" label={"Description"} />
          <p className="font-normal text-gray-600 py-1">
            The description for your webhook</p>
          <ErrorLabel error={formState.errors.description?.message} />
          <div className="md:w-[86ch]">
            <Input
              multiline={true}
              rows={5}
              inErrorState={!!formState.errors.description}
              placeholder="Enter a description for your webhook"
              {...register("description", {
                maxLength: { value: 500, message: "max 500 characters" }
              })} />
          </div>
        </div>
        <div className="md:col-span-5 mt-1">
          <Label forField="isActive" label={"Activate Webhook"} />
          <p className="font-normal text-gray-600 py-1">
            Webhooks which are inactive (unchecked) will not receive any notifications.</p>
          <ErrorLabel error={formState.errors.isActive?.message} />
          <input
              {...register("isActive")}
              className="size-5 rounded-sm border-gray-300 mt-2"
              type="checkbox" />
        </div>

        <div className="md:col-span-5 mt-1">
          <div className="text-gray-600">
            <p className="font-semibold text-lg">Events</p>
            <p className="font-normal text-gray-600 py-1">
              Which events would you like to subscribe to and be
              notified of?
            </p>
            <hr className="py-2" />
          </div>
        </div>
        <div className="md:col-span-5">
          {loading
            ? ( <LoadingPage /> )
            : ( <WebhookEventSelector groupedEvents={groupedEvents} register={register} /> )
          }
        </div>
        <WebhookGuidanceAccordion />
        {/* this should really be a form footer where you can add multiple buttons */}
        <div className="md:col-span-5 text-right mt-4">
          <div className="inline-flex gap-2 w-1/2 flex-row-reverse">
            <SubmitButton
              text={"Update Webhook"}
              className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold" />
            <ButtonCancel text="Cancel" className="text-kPurple-500" />
          </div>
        </div>
      </div>
    </FormSingleColumn>
    </>
  );
};
